import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';
import AppImage from '../../components/AppImage';
import Button from '../../components/ui/Button';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { settingsService } from '../../services/settingsService';

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPrivacyPolicy();
  }, []);

  const loadPrivacyPolicy = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const setting = await settingsService?.getSettingByKey('page_privacy_policy');
      
      if (setting?.settingValue) {
        setContent(setting?.settingValue);
      } else {
        // Default content if not found in database
        setContent({
          title: 'Privacy Policy',
          lastUpdated: '2025-12-08',
          sections: [
            {
              title: 'Introduction',
              content: 'RoleCall is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our musical theatre learning platform.'
            },
            {
              title: 'Information We Collect',
              content: 'We collect information that you provide directly to us, including your name, email address, profile information, and learning progress data. We also automatically collect certain information about your device and how you interact with our platform.'
            },
            {
              title: 'How We Use Your Information',
              content: 'We use the information we collect to provide, maintain, and improve our services, to communicate with you, to personalize your learning experience, and to ensure the security of our platform.'
            },
            {
              title: 'Information Sharing',
              content: 'We do not sell your personal information. We may share your information with service providers who assist us in operating our platform, with your consent, or as required by law.'
            },
            {
              title: 'Data Security',
              content: 'We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.'
            },
            {
              title: 'Your Rights',
              content: 'You have the right to access, update, or delete your personal information. You may also have the right to restrict or object to certain processing of your data.'
            },
            {
              title: 'Contact Us',
              content: 'If you have any questions about this Privacy Policy, please contact us through the support page or email us at privacy@rolecall.app'
            }
          ]
        });
      }
    } catch (err) {
      console.error('Error loading privacy policy:', err);
      setError(err?.message || 'Failed to load privacy policy');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900 flex items-center justify-center">
        <LoadingSpinner size="large" text="Loading Privacy Policy..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900 flex items-center justify-center p-6">
        <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-2xl p-8 max-w-md">
          <Icon name="AlertTriangle" size={48} className="text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-heading font-bold text-white text-center mb-2">
            Unable to Load Privacy Policy
          </h2>
          <p className="text-blue-100 text-center mb-6">{error}</p>
          <Button onClick={loadPrivacyPolicy} className="w-full" iconName="RefreshCw" iconPosition="left">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Privacy Policy - RoleCall</title>
        <meta name="description" content="RoleCall Privacy Policy - Learn how we protect and manage your personal information" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        {/* Navigation Bar */}
        <nav className="container mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <AppImage 
              src="/assets/images/rolecall_logo_wide-1756786874531.png" 
              alt="RoleCall Logo - Musical Theatre Learning Platform"
              className="h-10 w-auto"
            />
          </div>
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-full px-6 py-2"
              iconName="Home"
              iconPosition="left"
            >
              Back to Home
            </Button>
          </div>
        </nav>

        {/* Content */}
        <div className="container mx-auto px-6 py-12 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-2xl p-8 md:p-12"
          >
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-white mb-4">
              {content?.title || 'Privacy Policy'}
            </h1>
            
            {content?.lastUpdated && (
              <p className="text-blue-200 mb-8">
                Last Updated: {new Date(content?.lastUpdated)?.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            )}

            <div className="space-y-8">
              {content?.sections?.map((section, index) => (
                <div key={index}>
                  <h2 className="text-2xl font-heading font-semibold text-white mb-4">
                    {section?.title}
                  </h2>
                  <div className="text-blue-100 leading-relaxed whitespace-pre-wrap">
                    {section?.content}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Footer */}
        <footer className="py-8 bg-black bg-opacity-30 mt-12">
          <div className="container mx-auto px-6 text-center">
            <AppImage 
              src="/assets/images/rolecall_logo_wide-1756786874531.png" 
              alt="RoleCall Logo"
              className="h-8 w-auto mx-auto mb-4"
            />
            <p className="text-blue-200">
              © {new Date()?.getFullYear()} RoleCall. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default PrivacyPolicyPage;